import { useState } from 'react';
import { CheckCircle2, Plus } from 'lucide-react';
import { CONTRACT_TYPES, EMPTY_CONTRACT_FORM, SLA_TIERS } from './mockContractData';

const inputClass =
  'w-full rounded-lg border border-logi-grid bg-white px-3 py-2 text-sm text-logi-text placeholder-logi-subtle outline-none transition focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30';

const labelClass = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-logi-text0';

export default function ContractCreateForm({ contractType, onSubmit }) {
  const [form, setForm] = useState(EMPTY_CONTRACT_FORM);
  const [savedMsg, setSavedMsg] = useState('');
  const typeMeta = CONTRACT_TYPES[contractType];

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.partyName.trim() || !form.startDate || !form.expiryDate) return;

    onSubmit?.({
      type: contractType,
      partyName: form.partyName.trim(),
      slaTier: form.slaTier,
      startDate: form.startDate,
      expiryDate: form.expiryDate,
    });

    setForm(EMPTY_CONTRACT_FORM);
    setSavedMsg(`${typeMeta.label} created successfully.`);
    setTimeout(() => setSavedMsg(''), 3000);
  };

  return (
    <section className="rounded-xl border border-logi-grid bg-white p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
          <h3 className="text-sm font-bold text-logi-text">
            Register New {typeMeta.label.replace(' Contracts', '').replace(' Policies', '')}
          </h3>
        </div>
        {savedMsg && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            {savedMsg}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="sm:col-span-2">
          <label className={labelClass}>Party Name</label>
          <input
            className={inputClass}
            placeholder="Company or individual name…"
            value={form.partyName}
            onChange={handleChange('partyName')}
            required
          />
        </div>
        <div>
          <label className={labelClass}>SLA Tier</label>
          <select className={inputClass} value={form.slaTier} onChange={handleChange('slaTier')}>
            {Object.entries(SLA_TIERS).map(([key, cfg]) => (
              <option key={key} value={key}>
                {cfg.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Start Date</label>
          <input
            type="date"
            className={inputClass}
            value={form.startDate}
            onChange={handleChange('startDate')}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Expiry Date</label>
          <input
            type="date"
            className={inputClass}
            value={form.expiryDate}
            onChange={handleChange('expiryDate')}
            required
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-5">
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg shadow-emerald-500/20 transition hover:from-emerald-500 hover:to-teal-500"
          >
            <Plus className="h-4 w-4" />
            Create Contract
          </button>
        </div>
      </form>
    </section>
  );
}
